<template>
  <div class="app-header">
    <!--
      Brand sits flush left, in the Starjedi face used elsewhere in
      the chrome (e.g. queue panel title). The R2-D2 dropdown trigger
      is the only other thing in the header — pinned right via
      `justify-content: space-between` on the wrapper. No
      breadcrumbs / nav: page-specific navigation lives on the page,
      not in the header.
    -->
    <h1 class="app-header-brand">R2-D2</h1>

    <!--
      R2-D2 menu button. The trigger renders the gif's first frame as
      a static image until the user hovers over the trigger; on
      hover, the src swaps to the animated gif so it plays. On
      mouse-leave we swap back to the frozen frame so the page chrome
      stays calm by default. The first-frame snapshot is generated on
      mount via canvas.drawImage on the loaded gif (which captures
      whichever frame is currently displayed — frame 1 at load time).
    -->
    <div ref="menuRoot" class="app-header-menu-root">
      <button
        type="button"
        class="app-header-menu-trigger"
        aria-haspopup="true"
        :aria-expanded="menuOpen"
        @click="toggleMenu"
        @mouseenter="onTriggerEnter"
        @mouseleave="onTriggerLeave"
        @focus="onTriggerEnter"
        @blur="onTriggerLeave"
      >
        <img :src="triggerSrc" alt="" class="app-header-menu-gif" aria-hidden="true" />
        <span class="visually-hidden">Open user menu</span>
      </button>
      <ul v-if="menuOpen" class="app-header-menu">
        <li>
          <button type="button" class="app-header-menu-item" @click="onForceQuit">
            force quit
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapActions } from "pinia";
import { useGeneralStore } from "@/stores/general-store";
import r2d2Gif from "@/images/r2-d2.gif";

// Shared top-of-page header for every deployatron page.
// Pages slot in their breadcrumb crumbs (separators + page-specific
// links) via the `crumbs` slot; the brand text + R2D2 menu live here
// once so every page has the same chrome.
//
// The menu is a tiny self-contained dropdown — no VCL dropdown
// component is needed yet, and a custom one keeps the click-outside
// behaviour close to the trigger. When VCL grows iDropdownMenu we
// can swap this implementation without touching the consumer pages.
export default {
  name: "AppHeader",
  data() {
    return {
      menuOpen: false,
      r2d2Gif,
      // Static first-frame data URL extracted at mount via canvas.
      // Empty until the snapshot completes; we fall back to the gif
      // itself so the trigger is never blank during the brief load
      // window (it will animate for a moment, then freeze when the
      // snapshot replaces it).
      frozenSrc: "",
      hovering: false,
    };
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onDocumentKeyDown);
    this.captureFirstFrame();
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onDocumentKeyDown);
  },
  computed: {
    // While hovering / focused the real animated gif renders. When
    // not hovering, render the canvas-extracted frozen frame so the
    // header chrome doesn't constantly animate. Until the frozen
    // frame is ready we keep showing the gif as a fallback.
    triggerSrc() {
      if (this.hovering) return this.r2d2Gif;
      return this.frozenSrc || this.r2d2Gif;
    },
  },
  methods: {
    ...mapActions(useGeneralStore, ["logout"]),
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    },
    captureFirstFrame() {
      // Off-screen image → canvas → dataURL. drawImage on a freshly
      // decoded animated gif captures whichever frame is currently
      // being shown; on the load event that's frame 1, so the
      // resulting PNG is a static snapshot of the keyframe. No build
      // step required.
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) return;
          ctx.drawImage(img, 0, 0);
          this.frozenSrc = canvas.toDataURL("image/png");
        } catch (e) {
          // canvas.toDataURL throws on cross-origin taint; the gif is
          // same-origin (webpack-emitted asset) so this should not
          // happen, but if it does we just leave the gif rendering.
          this.frozenSrc = "";
        }
      };
      img.src = this.r2d2Gif;
    },
    onTriggerEnter() {
      // Restart the gif from frame 1 each time the user starts a
      // hover. Setting src to the same URL is a no-op in some
      // browsers; clearing it first guarantees a fresh decode pass
      // so the animation begins at the keyframe rather than wherever
      // the previous loop happened to be.
      this.hovering = false;
      this.$nextTick(() => {
        this.hovering = true;
      });
    },
    onTriggerLeave() {
      this.hovering = false;
    },
    onForceQuit() {
      this.menuOpen = false;
      this.logout();
    },
    onDocumentClick(event) {
      if (!this.menuOpen) return;
      const root = this.$refs.menuRoot;
      if (root && root.contains(event.target)) return;
      this.menuOpen = false;
    },
    onDocumentKeyDown(event) {
      if (event.key === "Escape" && this.menuOpen) {
        this.menuOpen = false;
      }
    },
  },
};
</script>

<style scoped>
/* Plain flex row, brand left + R2-D2 menu trigger right. No breadcrumbs
   live here any more — pages render their own navigation if needed. */
.app-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  /* Top padding gives the Starjedi brand breathing room from the
     viewport edge; the queue panel below already reserves its own
     30 px top margin so there's no need for additional space here. */
  padding: 24px 0 0 0;
}

/* Brand uses the Star Wars-styled face declared in base.scss (same
   font as the queue panel title). Yellow on black to match the rest
   of the chrome. Tag is <h1> so screen readers and document outline
   get a real top-level heading. */
.app-header-brand {
  margin: 0;
  font-family: 'Starjedi', 'TradeGothic', sans-serif;
  font-weight: bold;
  font-size: 32px;
  line-height: 1;
  color: yellow;
  white-space: nowrap;
}

.app-header-menu-root {
  position: relative;
}

.app-header-menu-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  /* Borderless: the gif itself is the icon. The transparent border
     is reserved so the round mask still has the same hit area as a
     visually bordered button (kept zero-width via padding/margin
     adjustments below). */
  border: 0;
  background: transparent;
  color: yellow;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
}

/* The gif fills the round trigger. object-fit: cover preserves the
   character's aspect while keeping the round mask completely covered
   so no black band shows around the edges. */
.app-header-menu-gif {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.app-header-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 10;
  min-width: 160px;
  background: black;
  border: 1px solid yellow;
  border-radius: 4px;
  list-style: none;
  margin: 0;
  padding: 4px 0;
}

.app-header-menu-item {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  padding: 8px 12px;
  color: yellow;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
}

.app-header-menu-item:hover {
  background: rgba(255, 255, 0, 0.15);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
